/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import { HeroSection } from '../components/Home/HeroSection'
import { ProductCard } from '../components/Products/ProductCard'
import { GettingStartedSection } from '../components/Home/GettingStartedSection'
import { SubscribeModal } from '../components/Modals/SubscribeModal'
import { CalculatorModal } from '../components/Modals/CalculatorModal'
import { WallyReviews } from '../components/WallyReviews'
import { productData } from '../data/products'
import { parseURLParams, isAffiliateFlow } from '../utils/urlParams'
import { useLoading } from '../hooks/useLoading'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { Product } from '../types'

export const HomePage = () => {
  const { setLoading } = useLoading()
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [showSubscribeModal, setShowSubscribeModal] = useState(false)
  const [showCalculatorModal, setShowCalculatorModal] = useState(false)
  const [calculatorData, setCalculatorData] = useState({ strategyName: '', cagr: 0 })

  const urlParams = parseURLParams()
  const isAffiliate = isAffiliateFlow(urlParams)

  const handleSubscribeClick = (product: Product) => {
    setSelectedProduct(product)
    setShowSubscribeModal(true)
  }

  const handleCalculatorClick = (strategyName: string, cagr: number) => {
    setCalculatorData({ strategyName, cagr })
    setShowCalculatorModal(true)
  }

  const closeSubscribeModal = () => {
    setShowSubscribeModal(false)
    setSelectedProduct(null)
  }

  const closeCalculatorModal = () => {
    setShowCalculatorModal(false)
    setCalculatorData({ strategyName: '', cagr: 0 })
  }

  useDocumentTitle(isAffiliate ? "Affiliate Signup" : "Home", [isAffiliate])

  return (
    <>
      <HeroSection isAffiliate={isAffiliate} />

      {/* Affiliate Info Section */}
      {isAffiliate && (
        <div className="container mb-5">
          <div className="row">
            <div className="col-12">
              <div className="card bg-primary text-white">
                <div className="card-body">
                  <h4 className="card-title">Affiliate Program Signup</h4>
                  <p className="card-text">You're signing up as an affiliate. Please select a subscription below to get started.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Telegram Instructions */}
      <div className="container mb-5 px-4 telegram-instructions">
        Please download and install the <a href="https://telegram.org/" rel="noopener noreferrer" target="_blank">Telegram app</a> on your device before purchasing a subscription. After completing your purchase, you will receive a personalized, time-sensitive invitation link to a private Telegram channel. This channel provides timely rebalance alerts with the latest allocation updates.
      </div>
      
      {/* Products Section */}
      <div id="products-and-prices" className="container mb-5">
        <div className="row g-4">
          {productData.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              isAffiliate={isAffiliate}
              referralCode={urlParams.code}
              onSubscribe={handleSubscribeClick}
              onOpenCalculator={handleCalculatorClick}
            />
          ))}
        </div>
      </div>

      {/* Wally Reviews Widget */}
      <div className="container mb-5">
        <WallyReviews widgetId="68d606bd13b13d0014f12158" />
      </div>

      <GettingStartedSection />

      {/* Modals */}
      {showSubscribeModal && (
        <SubscribeModal
          selectedProduct={selectedProduct}
          isAffiliate={isAffiliate}
          affiliateReferrer={urlParams.ref}
          referralCode={urlParams.code}
          token={urlParams.token}
          onClose={closeSubscribeModal}
          onLoading={setLoading}
        />
      )}

      {showCalculatorModal && (
        <CalculatorModal
          isOpen={showCalculatorModal}
          strategyName={calculatorData.strategyName}
          cagr={calculatorData.cagr}
          onClose={closeCalculatorModal}
        />
      )}
    </>
  )
}